import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Instagram } from 'lucide-react';

const Header = () => {
  const location = useLocation();
  const [workExpanded, setWorkExpanded] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-sm z-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-14">
          {/* Artist Signature */}
          <Link to="/" className="flex-shrink-0">
            <h1 className="text-2xl font-signature italic text-gray-900 hover:text-teal-600 transition-colors duration-300">
              niki
            </h1>
            <p className="text-[10px] font-signature text-gray-400 -mt-1 tracking-wide">Nikhita Kapur's Studio</p>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-base font-signature transition-colors duration-300 ${
                isActive('/')
                  ? 'text-teal-700'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Home
            </Link>

            <Link
              to="/about"
              className={`text-base font-signature transition-colors duration-300 ${
                isActive('/about')
                  ? 'text-teal-700'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              About
            </Link>

            {/* Available Dropdown */}
            <div
              className="relative group"
            >
              <button
                onMouseEnter={() => setWorkExpanded(true)}
                className={`flex items-center space-x-1 text-base font-signature transition-colors duration-300 ${
                  location.pathname.startsWith('/work')
                    ? 'text-teal-700'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span>Available</span>
                <ChevronDown className="w-3.5 h-3.5" />
              </button>

              {workExpanded && (
                <div 
                  className="absolute top-full left-0 mt-1 w-52 bg-white/95 backdrop-blur-sm border border-gray-100 rounded-lg shadow-lg py-1.5 z-50"
                  onMouseEnter={() => setWorkExpanded(true)}
                  onMouseLeave={() => setWorkExpanded(false)}
                >
                  <Link
                    to="/work/architecture-of-panic"
                    className="block px-4 py-1.5 text-sm font-signature text-gray-600 hover:bg-teal-50 hover:text-teal-700 transition-colors duration-200"
                    onClick={() => setWorkExpanded(false)}
                  >
                    Architecture of Panic
                  </Link>
                  <Link
                    to="/work/memory-in-transit"
                    className="block px-4 py-1.5 text-sm font-signature text-gray-600 hover:bg-teal-50 hover:text-teal-700 transition-colors duration-200"
                    onClick={() => setWorkExpanded(false)}
                  >
                    Memory in Transit
                  </Link>
                  <Link
                    to="/work/calm-in-chaos"
                    className="block px-4 py-1.5 text-sm font-signature text-gray-600 hover:bg-teal-50 hover:text-teal-700 transition-colors duration-200"
                    onClick={() => setWorkExpanded(false)}
                  >
                    Calm in Chaos
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/commission"
              className={`text-base font-signature transition-colors duration-300 ${
                isActive('/commission')
                  ? 'text-teal-700'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Commission
            </Link>

            {/* Instagram Icon */}
            <a
              href="https://www.instagram.com/downtimeartist"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-pink-500 transition-colors duration-300"
              aria-label="Instagram"
            >
              <Instagram className="w-5 h-5" />
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 relative z-50"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-gray-700" />
            ) : (
              <Menu className="w-6 h-6 text-gray-700" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200 bg-white" data-testid="mobile-menu">
            <Link
              to="/"
              className="block py-2 text-base font-signature text-gray-700 hover:text-teal-700"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-home"
            >
              Home
            </Link>
            <Link
              to="/about"
              className="block py-2 text-base font-signature text-gray-700 hover:text-teal-700"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-about"
            >
              About
            </Link>
            <div className="py-2">
              <button
                onClick={() => setWorkExpanded(!workExpanded)}
                className="flex items-center justify-between w-full text-base font-signature text-gray-700"
                data-testid="mobile-work-toggle"
              >
                <span>Available</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${workExpanded ? 'rotate-180' : ''}`} />
              </button>
              {workExpanded && (
                <div className="pl-4 mt-2 space-y-2">
                  <Link
                    to="/work/architecture-of-panic"
                    className="block py-1 text-sm font-signature text-gray-600 hover:text-teal-700"
                    onClick={() => { setMobileMenuOpen(false); setWorkExpanded(false); }}
                    data-testid="mobile-nav-architecture-of-panic"
                  >
                    Architecture of Panic
                  </Link>
                  <Link
                    to="/work/memory-in-transit"
                    className="block py-1 text-sm font-signature text-gray-600 hover:text-teal-700"
                    onClick={() => { setMobileMenuOpen(false); setWorkExpanded(false); }}
                    data-testid="mobile-nav-memory"
                  >
                    Memory in Transit
                  </Link>
                  <Link
                    to="/work/calm-in-chaos"
                    className="block py-1 text-sm font-signature text-gray-600 hover:text-teal-700"
                    onClick={() => { setMobileMenuOpen(false); setWorkExpanded(false); }}
                    data-testid="mobile-nav-calm-in-chaos"
                  >
                    Calm in Chaos
                  </Link>
                </div>
              )}
            </div>
            <Link
              to="/commission"
              className="block py-2 text-base font-signature text-gray-700 hover:text-teal-700"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-commission"
            >
              Commission
            </Link>
            <a
              href="https://www.instagram.com/downtimeartist"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 py-2 text-base font-signature text-gray-700 hover:text-pink-500"
              data-testid="mobile-nav-instagram"
            >
              <Instagram className="w-5 h-5" />
              <span>Instagram</span>
            </a>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;